import { useState } from "react";
import { User, KeyRound, Eye, EyeOff, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-2.5">
      <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800 text-right">{value || "—"}</div>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (current.length < 1) {
      toast.error("Password saat ini wajib diisi");
      return;
    }
    if (next.length < 6) {
      toast.error("Password baru minimal 6 karakter");
      return;
    }
    if (next !== confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    if (current === next) {
      toast.error("Password baru tidak boleh sama dengan password lama");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password berhasil diperbarui");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const roleLabel = (user?.role || "").replace("_", " ");

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="page-profil">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
          Akun Saya
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
          Profil Pengguna
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Perbarui informasi keamanan akun Anda di sini.
        </p>
      </div>

      {/* Profile summary */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="profil-summary-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
            <User size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl font-extrabold text-slate-900 leading-tight truncate">
              {user?.name}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wider">
              {roleLabel}
            </div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          <InfoRow label="NPP" value={user?.npp} />
          <InfoRow label="Email" value={user?.email} />
          <InfoRow label="Role" value={roleLabel} />
          <InfoRow label="Status" value={user?.is_active ? "Aktif" : "Nonaktif"} />
        </div>
      </div>

      {/* Change password */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="ganti-password-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
            <KeyRound size={18} />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">
              Ganti Password
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Perbarui kata sandi Anda secara berkala untuk menjaga keamanan akun.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 max-w-md">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Password Saat Ini
            </Label>
            <div className="relative mt-1.5">
              <Input
                type={showCur ? "text" : "password"}
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="input-current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCur((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-800"
                data-testid="toggle-current-password"
                aria-label={showCur ? "Sembunyikan password" : "Tampilkan password"}
              >
                {showCur ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Password Baru <span className="text-slate-400 font-normal">(min. 6 karakter)</span>
            </Label>
            <div className="relative mt-1.5">
              <Input
                type={showNew ? "text" : "password"}
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="input-new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-800"
                data-testid="toggle-new-password"
                aria-label={showNew ? "Sembunyikan password" : "Tampilkan password"}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Konfirmasi Password Baru
            </Label>
            <Input
              type={showNew ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="input-confirm-password"
              className="mt-1.5"
            />
            {confirm && confirm !== next ? (
              <div className="text-[11px] text-rose-700 mt-1">
                Konfirmasi belum cocok dengan password baru
              </div>
            ) : null}
          </div>

          <div className="pt-2">
            <Button
              onClick={submit}
              disabled={saving}
              className="gap-2 bg-teal-700 hover:bg-teal-800"
              data-testid="save-password-btn"
            >
              <Save size={14} /> {saving ? "Menyimpan…" : "Simpan Password Baru"}
            </Button>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 max-w-md" data-testid="password-info">
          <ShieldCheck size={14} className="mt-0.5 shrink-0 text-teal-700" />
          <div>
            Password Anda dienkripsi (bcrypt) di server. Setelah diperbarui, sesi yang sedang aktif tetap valid — silakan logout lalu login ulang jika Anda ingin memastikan hanya perangkat saat ini yang aktif.
          </div>
        </div>
      </div>
    </div>
  );
}
